import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Form,
} from 'react-bootstrap';
import { useFilterHistory } from '../hooks/useFilterHistory';
import { EpisodeTextImportCommandKey } from '../../../../services/EpisodeTextService/types/episodeTextImportCommandTypes';
import { EpisodeTextEntityType } from '../../../../services/EpisodeTextService/types/episodeTextImportTypes';
import {
  ColumnEditability,
  CsvEditNodeGroup,
  CsvEditRow,
  EditableField,
  FilterToken,
  SearchHistoryEntry,
  SearchMode,
  STEP_EDITABILITY,
} from '../types/selectiveEditTypes';
import { parseFilterTokens } from '../utils/parseFilterTokens';
import css from './TableEditor.module.scss';
import { EditableCell } from './EditableCell';
import { SmartFilterInput } from './SmartFilterInput';

const MIN_SEARCH_LENGTH = 2;
const PAGE_SIZE = 50;
const FILTER_STORAGE_KEY = 'selective-edit-filters';

type PersistedFilters = {
  searchInput?: string; // legacy — migrated to filterTokens on read
  filterTokens?: FilterToken[];
  searchMode?: SearchMode;
  caseSensitive?: boolean;
  quickFilter?: 'all' | 'errors' | 'changed';
  stepTypeFilter?: EpisodeTextEntityType | 'all';
  scrollNodeId?: string;
  scrollNodeOffset?: number;
  expandedNodeIds?: string[];
};

function readPersistedFilters(): PersistedFilters {
  try {
    const raw = localStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as PersistedFilters;
    // Migrate legacy searchInput → filterTokens
    if (parsed.searchInput && !parsed.filterTokens) {
      parsed.filterTokens = parseFilterTokens(parsed.searchInput);
      delete parsed.searchInput;
    }
    return parsed;
  } catch {
    return {};
  }
}

function writePersistedFilters(filters: PersistedFilters): void {
  try {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

// Badge config for step types. excludeFromHeader hides from node accordion header.
// Dialogue/Narration/EmptyStep not listed — they are in every node and add noise.
type StepBadgeConfig = { label: string; color: string; excludeFromHeader?: boolean };

const STEP_BADGE_CONFIG: Partial<Record<EpisodeTextEntityType, StepBadgeConfig>> = {
  [EpisodeTextEntityType.Choice]:             { label: 'Choice',   color: '#6f42c1' },
  [EpisodeTextEntityType.Check]:              { label: 'Check',    color: '#0d6efd' },
  [EpisodeTextEntityType.CheckConditionList]: { label: 'Switch',   color: '#0d6efd' },
  [EpisodeTextEntityType.Remember]:           { label: 'Remember', color: '#198754' },
  [EpisodeTextEntityType.Random]:             { label: 'Random',   color: '#d86b11' },
  [EpisodeTextEntityType.Ending]:             { label: 'Ending',   color: '#dc3545' },
  [EpisodeTextEntityType.Thinking]:           { label: 'Think',    color: '#0dcaf0' },
  [EpisodeTextEntityType.Reaction]:           { label: 'Reaction', color: '#0aa8c7' },
  [EpisodeTextEntityType.Video]:              { label: 'Video',    color: '#e83e8c' },
  [EpisodeTextEntityType.Achieve]:            { label: 'Achieve',  color: '#fd7e14' },
  [EpisodeTextEntityType.GotoFromNode]:       { label: 'Goto',     color: '#6c757d', excludeFromHeader: true },
};

type TableEditorProps = {
  nodeGroups: CsvEditNodeGroup[];
  onUpdateRowField: (rowId: string, field: EditableField, value: string) => void;
  isCellDirty: (rowId: string, field: string) => boolean;
  hasNodeErrors: (nodeId: string) => boolean;
  isNodeDirty: (nodeId: string) => boolean;
  onRevertNode: (nodeId: string) => void;
  onNavigateToNode?: (nodeTitle: string) => void;
  onFilteredCountChange?: (filteredCount: number) => void;
  isLoading?: boolean;
  isReadOnly?: boolean;
  /** When set, scroll to this node on mount and highlight it. Resets filters. */
  initialNodeId?: string;
};

type ActiveCell = {
  rowId: string;
  field: EditableField;
  badgePrefix?: string;
};

// --- Matcher types ---

type SearchMatcher = {
  /** Test whether a searchableText string matches the current search. */
  test: (text: string) => boolean;
  /** Regex error message (only set when regex mode has invalid pattern). */
  error: string | null;
  /** Whether the matcher is active (query meets min length & no error). */
  active: boolean;
  /** The lowercased query for plain-mode highlight (empty when inactive). */
  highlightQuery: string;
  /** Compiled regex for regex-mode highlight (null when inactive or plain). */
  highlightRegex: RegExp | null;
};

/**
 * Build a search matcher based on current search parameters.
 * Returns test function, error state, and highlight helpers.
 */
function buildMatcher(query: string, mode: SearchMode, caseSensitive: boolean): SearchMatcher {
  const inactive: SearchMatcher = {
    test: () => false,
    error: null,
    active: false,
    highlightQuery: '',
    highlightRegex: null,
  };

  if (query.length < MIN_SEARCH_LENGTH) return inactive;

  if (mode === 'plain') {
    const needle = caseSensitive ? query : query.toLowerCase();
    return {
      test: (text) => {
        const haystack = caseSensitive ? text : text.toLowerCase();
        return haystack.includes(needle);
      },
      error: null,
      active: true,
      highlightQuery: caseSensitive ? query : query.toLowerCase(),
      highlightRegex: null,
    };
  }

  // Regex mode
  try {
    const flags = caseSensitive ? 'g' : 'gi';
    const re = new RegExp(query, flags);
    return {
      test: (text) => {
        re.lastIndex = 0;
        return re.test(text);
      },
      error: null,
      active: true,
      highlightQuery: '',
      highlightRegex: re,
    };
  } catch (e) {
    return {
      test: () => false,
      error: e instanceof Error ? e.message : 'Invalid regular expression',
      active: false,
      highlightQuery: '',
      highlightRegex: null,
    };
  }
}

// --- Token-based matcher ---

type TokenMatcher = {
  active: boolean;
  test: (group: CsvEditNodeGroup) => boolean;
  error: string | null;
};

function buildTokenMatcher(
  tokens: FilterToken[],
  mode: SearchMode,
  caseSensitive: boolean,
): TokenMatcher {
  const inactive: TokenMatcher = { active: false, test: () => true, error: null };
  if (tokens.length === 0) return inactive;

  // Group tokens by prefix
  const byPrefix = new Map<string, FilterToken[]>();
  for (const t of tokens) {
    const existing = byPrefix.get(t.prefix) ?? [];
    existing.push(t);
    byPrefix.set(t.prefix, existing);
  }

  // Build per-value matchers
  type ValueTest = (text: string) => boolean;
  function makeValueTest(value: string): ValueTest | { error: string } | null {
    if (value.length < MIN_SEARCH_LENGTH) return null;
    if (mode === 'plain') {
      const needle = caseSensitive ? value : value.toLowerCase();
      return (text) => (caseSensitive ? text : text.toLowerCase()).includes(needle);
    }
    try {
      const flags = caseSensitive ? 'g' : 'gi';
      const re = new RegExp(value, flags);
      return (text) => { re.lastIndex = 0; return re.test(text); };
    } catch (e) {
      return { error: e instanceof Error ? e.message : 'Invalid regular expression' };
    }
  }

  // Build prefix group testers
  // Within same prefix: OR. Across prefixes: AND.
  const prefixTesters: Array<(g: CsvEditNodeGroup) => boolean> = [];
  let firstError: string | null = null;

  for (const [prefix, group] of byPrefix) {
    const valueTests: ValueTest[] = [];
    for (const token of group) {
      const vt = makeValueTest(token.value);
      if (vt === null) continue;
      if (typeof vt === 'object' && 'error' in vt) {
        if (!firstError) firstError = vt.error;
        continue;
      }
      valueTests.push(vt);
    }
    if (valueTests.length === 0) continue;

    const orTest = (text: string) => valueTests.some((vt) => vt(text));

    switch (prefix) {
      case 'node':
        prefixTesters.push((g) => orTest(g.nodeNameText));
        break;
      case 'step':
        prefixTesters.push((g) => orTest(g.stepContentText));
        break;
      case 'type':
        prefixTesters.push((g) => {
          const typeLabels = g.stepRows.map((r) => {
            const cfg = STEP_BADGE_CONFIG[r.type as EpisodeTextEntityType];
            return cfg ? cfg.label.toLowerCase() : (r.type ?? '').toLowerCase();
          });
          return group.some((token) => {
            const val = token.value.toLowerCase();
            return typeLabels.some((label) => label.includes(val));
          });
        });
        break;
      case 'speaker':
        prefixTesters.push((g) => orTest(g.speakerText));
        break;
      case 'general':
      default:
        prefixTesters.push((g) => orTest(g.searchableText));
        break;
    }
  }

  if (firstError && prefixTesters.length === 0) {
    return { active: false, test: () => true, error: firstError };
  }

  if (prefixTesters.length === 0) return inactive;

  return {
    active: true,
    test: (g) => prefixTesters.every((tester) => tester(g)),
    error: firstError,
  };
}

// --- Badge extraction helpers ---

function extractCommandBadge(row: CsvEditRow): { badge: string; editableText: string } | null {
  if (!row.type) return null;

  if (row.type === EpisodeTextEntityType.Choice) {
    const match = row.text.match(/^(#CHOICE\s*)(.*)/i);
    if (match) {
      return { badge: match[1], editableText: match[2] };
    }
  }

  if (row.type === EpisodeTextEntityType.CheckConditionList) {
    return null;
  }

  if (row.type === EpisodeTextEntityType.CheckCondition) {
    const ifMatch = row.text.match(/^(if\s*=\s*)(.*)/i);
    if (ifMatch) {
      return { badge: ifMatch[1], editableText: ifMatch[2] };
    }
    if (row.text.trim().toLowerCase() === 'else') {
      return { badge: 'else', editableText: '' };
    }
  }

  return null;
}

/**
 * Extract a colored command badge for readonly cells.
 * Returns the command keyword, remaining text, and badge color from STEP_BADGE_CONFIG.
 * Used for all cell types (readonly, editable, partial) to show colored command badges.
 * Used when editabilityValue === false to visually highlight commands like #GOTO, #VIDEO, etc.
 */
function extractReadonlyCommandBadge(
  row: CsvEditRow,
  field: EditableField,
): { badge: string; remainingText: string; color: string } | null {
  if (!row.type) return null;
  const badgeMeta = STEP_BADGE_CONFIG[row.type];
  if (!badgeMeta) return null;

  const value = field === 'nodeOrSpeaker' ? row.nodeOrSpeaker : field === 'text' ? row.text : row.description;
  if (!value || !value.trim()) return null;

  // Match leading #COMMAND pattern (e.g. "#GOTO NodeName", "#VIDEO url")
  const cmdMatch = value.match(/^(#\w+)\s*(.*)/i);
  if (cmdMatch) {
    return { badge: cmdMatch[1], remainingText: cmdMatch[2], color: badgeMeta.color };
  }

  // For types without # prefix in text (e.g. GotoFromNode text is just "→ NodeName")
  // Show the type label as badge with full text as remaining
  if (field === 'text' && row.type === EpisodeTextEntityType.GotoFromNode) {
    return { badge: 'Goto', remainingText: value, color: badgeMeta.color };
  }

  return null;
}

/** Extract the goto target node name from a row, if it's a goto-type element. */
function extractGotoTarget(row: CsvEditRow): string | null {
  // Use parsed commands map — reliable regardless of field or text format
  const gotoCmd = row.commands.get(EpisodeTextImportCommandKey.Goto);
  if (gotoCmd) {
    // gotoCmd is Map<EpisodeTextColumnKey, string[]> — target node name is the first value in any column
    for (const values of gotoCmd.values()) {
      if (values.length > 0 && values[0].trim()) {
        return values[0].trim();
      }
    }
  }
  // GotoFromNode — text column contains "→ NodeName"
  if (row.type === EpisodeTextEntityType.GotoFromNode) {
    const text = row.text.replace(/^→\s*/, '').trim();
    return text || null;
  }
  return null;
}

function extractDescriptionBadge(row: CsvEditRow): { badge: string; editableText: string } | null {
  if (row.type === EpisodeTextEntityType.CheckCondition || row.type === EpisodeTextEntityType.Answer) {
    const gotoMatch = row.description.match(/^(#goto\s+)(.*)$/i);
    if (gotoMatch) {
      return { badge: gotoMatch[1], editableText: gotoMatch[2] };
    }
  }
  if (row.type === EpisodeTextEntityType.Node) {
    const locationMatch = row.description.match(/^(#LOCATION\s+)(.*)$/i);
    if (locationMatch) {
      return { badge: locationMatch[1], editableText: locationMatch[2] };
    }
  }
  return null;
}

// --- Search highlight ---

/**
 * Splits `source` into character-level segments, each tagged with whether
 * it falls inside the prefix range [0, prefixLength).
 * Used to apply command keyword styling while preserving cross-boundary search highlights.
 */
function renderHighlightedText(
  value: string,
  highlightQuery: string,
  highlightRegex: RegExp | null,
  caseSensitive: boolean,
  prefixLength = 0,
  prefixColor?: string,
): React.ReactNode {
  const source = value ?? '';
  if (!source) return value;

  // Collect match ranges
  const matchRanges: Array<[number, number]> = [];

  if (highlightRegex) {
    highlightRegex.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = highlightRegex.exec(source)) !== null) {
      if (match[0].length === 0) { highlightRegex.lastIndex++; continue; }
      matchRanges.push([match.index, match.index + match[0].length]);
    }
  } else if (highlightQuery) {
    const sourceLower = caseSensitive ? source : source.toLowerCase();
    const needle = highlightQuery;
    let idx = sourceLower.indexOf(needle, 0);
    while (idx !== -1) {
      matchRanges.push([idx, idx + needle.length]);
      idx = sourceLower.indexOf(needle, idx + needle.length);
    }
  }

  // If no prefix styling and no matches — return plain string
  if (prefixLength === 0 && matchRanges.length === 0) return value;

  // Build segments: split source at prefix boundary + match boundaries
  const breakpoints = new Set<number>([0, source.length]);
  if (prefixLength > 0 && prefixLength < source.length) breakpoints.add(prefixLength);
  for (const [s, e] of matchRanges) { breakpoints.add(s); breakpoints.add(e); }

  const sorted = Array.from(breakpoints).sort((a, b) => a - b);
  const parts: React.ReactNode[] = [];

  for (let i = 0; i < sorted.length - 1; i++) {
    const segStart = sorted[i];
    const segEnd = sorted[i + 1];
    const text = source.slice(segStart, segEnd);
    const inPrefix = prefixLength > 0 && segStart < prefixLength;
    const inMatch = matchRanges.some(([s, e]) => segStart >= s && segEnd <= e);

    const style = inPrefix && prefixColor ? { color: prefixColor } : undefined;
    const className = [
      inPrefix ? css.commandKeyword : '',
      inMatch ? css.searchMatch : '',
    ].filter(Boolean).join(' ') || undefined;

    if (style || className) {
      parts.push(<span key={segStart} className={className} style={style}>{text}</span>);
    } else {
      parts.push(text);
    }
  }

  return <>{parts}</>;
}

// --- Editability helpers ---

function getEditability(type: EpisodeTextEntityType | undefined): ColumnEditability {
  if (!type) return { speaker: false, text: false, description: false };
  return STEP_EDITABILITY[type] ?? { speaker: false, text: false, description: false };
}

function isIndented(type: EpisodeTextEntityType | undefined): boolean {
  return type === EpisodeTextEntityType.Answer || type === EpisodeTextEntityType.CheckCondition;
}

// --- NodeAccordionItem ---

type ScopedMatchers = { node: SearchMatcher; step: SearchMatcher; speaker: SearchMatcher; description: SearchMatcher };

type NodeAccordionItemProps = {
  group: CsvEditNodeGroup;
  expanded: boolean;
  nodeDirty: boolean;
  nodeHasErrors: boolean;
  scopedMatchers: ScopedMatchers;
  caseSensitive: boolean;
  isHighlighted?: boolean;
  isSelected?: boolean;
  activeCellRef: React.MutableRefObject<ActiveCell | null>;
  isCellDirty: (rowId: string, field: string) => boolean;
  onToggle: (nodeId: string) => void;
  onRevertNode: (nodeId: string) => void;
  onNavigateToNode?: (nodeTitle: string) => void;
  onCellActivate: (rowId: string, field: EditableField, currentValue: string, badgePrefix?: string) => void;
  onCellCommit: (value: string) => void;
  onCellCancel: () => void;
  onGotoClick?: (targetNodeName: string, event?: React.MouseEvent) => void;
  isReadOnly?: boolean;
};

// Compute unique notable step-type badges for a node's stepRows
function getStepTypeBadges(stepRows: CsvEditRow[]): Array<{ label: string; color: string }> {
  const seen = new Set<EpisodeTextEntityType>();
  const result: Array<{ label: string; color: string }> = [];
  for (const row of stepRows) {
    if (!row.type) continue;
    if (seen.has(row.type)) continue;
    const badge = STEP_BADGE_CONFIG[row.type];
    if (badge && !badge.excludeFromHeader) {
      seen.add(row.type);
      result.push(badge);
    }
  }
  return result;
}

function NodeAccordionItem({
  group,
  expanded,
  nodeDirty,
  nodeHasErrors,
  scopedMatchers,
  caseSensitive,
  isHighlighted = false,
  isSelected = false,
  activeCellRef,
  isCellDirty,
  onToggle,
  onRevertNode,
  onNavigateToNode,
  onCellActivate,
  onCellCommit,
  onCellCancel,
  onGotoClick,
  isReadOnly = false,
}: NodeAccordionItemProps) {
  const nodeId = group.nodeRow.id;
  const stepTypeBadges = getStepTypeBadges(group.stepRows);

  // Clone regexes once per render to avoid allocating per-cell
  const cloneRe = (re: RegExp | null) => re ? new RegExp(re.source, re.flags) : null;
  const clonedRegexes = useMemo(() => ({
    node: cloneRe(scopedMatchers.node.highlightRegex),
    step: cloneRe(scopedMatchers.step.highlightRegex),
    speaker: cloneRe(scopedMatchers.speaker.highlightRegex),
    description: cloneRe(scopedMatchers.description.highlightRegex),
  }), [scopedMatchers]);

  type HighlightScope = 'node' | 'step' | 'speaker' | 'description';
  const highlight = useCallback(
    (text: string, scope: HighlightScope = 'step', prefixLength = 0, prefixColor?: string) => {
      const m = scopedMatchers[scope];
      const re = clonedRegexes[scope];
      return m.active || prefixLength > 0
        ? renderHighlightedText(text, m.active ? m.highlightQuery : '', m.active ? re : null, caseSensitive, prefixLength, prefixColor)
        : text;
    },
    [scopedMatchers, clonedRegexes, caseSensitive],
  );

  const fieldToScope = (f: EditableField, row: CsvEditRow): HighlightScope =>
    (row.type === EpisodeTextEntityType.Node && f === 'nodeOrSpeaker') ? 'node'
      : (row.type !== EpisodeTextEntityType.Node && f === 'nodeOrSpeaker') ? 'speaker'
        : f === 'description' ? 'description'
          : 'step';

  const renderCell = (row: CsvEditRow, field: EditableField, fieldEditability: boolean | 'partial') => {
    // In isReadOnly mode (Live episodes), force all cells to non-editable
    const editabilityValue = isReadOnly ? false as const : fieldEditability;
    const rowId = row.id;
    const scope = fieldToScope(field, row);
    const currentActiveCell = activeCellRef.current;
    const isActive = currentActiveCell?.rowId === rowId && currentActiveCell?.field === field;
    const dirty = isCellDirty(rowId, field);
    const hasErrors = row.errors.length > 0;

    if (editabilityValue === false) {
      const rawValue = field === 'nodeOrSpeaker' ? row.nodeOrSpeaker : field === 'text' ? row.text : row.description;
      const readonlyBadge = extractReadonlyCommandBadge(row, field);
      if (readonlyBadge) {
        const gotoTarget = onGotoClick ? extractGotoTarget(row) : null;
        if (gotoTarget && onGotoClick) {
          const handleGoto = onGotoClick;
          return (
            <td className={`${css.readonlyCell} ${dirty ? css.dirtyCell : ''}`}>
              <span
                className={css.gotoBadge}
                title={`Scroll to ${gotoTarget}`}
                onClick={(e) => { e.stopPropagation(); handleGoto(gotoTarget, e); }}
                style={{ color: readonlyBadge.color }}
              >
                {rawValue.slice(0, readonlyBadge.badge.length)}
              </span>
              {highlight(rawValue.slice(readonlyBadge.badge.length), scope)}
            </td>
          );
        }
        return (
          <td className={`${css.readonlyCell} ${dirty ? css.dirtyCell : ''}`}>
            {highlight(rawValue, scope, readonlyBadge.badge.length, readonlyBadge.color)}
          </td>
        );
      }
      return (
        <td className={`${css.readonlyCell} ${dirty ? css.dirtyCell : ''}`}>
          {highlight(rawValue, scope)}
        </td>
      );
    }

    if (editabilityValue === 'partial') {
      const badgeInfo = field === 'text'
        ? extractCommandBadge(row)
        : field === 'description'
          ? extractDescriptionBadge(row)
          : null;

      if (badgeInfo) {
        // No editable text (e.g. "else") — render as plain readonly text
        if (!badgeInfo.editableText.trim()) {
          return (
            <td className={`${css.readonlyCell} ${dirty ? css.dirtyCell : ''}`}>
              {badgeInfo.badge.trim()}
            </td>
          );
        }
        if (isActive) {
          return (
            <EditableCell
              key={`${rowId}-${field}-active`}
              initialValue={badgeInfo.editableText}
              badgePrefix={badgeInfo.badge}
              hasBadge
              tdClassName={`${css.editableCell} ${dirty ? css.dirtyCell : ''} ${hasErrors ? css.validationErrorCell : ''}`}
              onCommit={onCellCommit}
              onCancel={onCellCancel}
            />
          );
        }
        const gotoTarget = onGotoClick ? extractGotoTarget(row) : null;
        const fullText = field === 'description' ? row.description : row.text;
        if (gotoTarget && onGotoClick) {
          const handleGoto = onGotoClick;
          return (
            <td
              className={`${css.editableCell} ${dirty ? css.dirtyCell : ''} ${hasErrors ? css.validationErrorCell : ''}`}
              onClick={() => onCellActivate(rowId, field, badgeInfo.editableText, badgeInfo.badge)}
            >
              <span
                className={css.gotoBadge}
                title={`Scroll to ${gotoTarget}`}
                onClick={(e) => { e.stopPropagation(); handleGoto(gotoTarget, e); }}
              >
                {fullText.slice(0, badgeInfo.badge.length)}
              </span>
              {highlight(fullText.slice(badgeInfo.badge.length), scope)}
            </td>
          );
        }
        return (
          <td
            className={`${css.editableCell} ${dirty ? css.dirtyCell : ''} ${hasErrors ? css.validationErrorCell : ''}`}
            onClick={() => onCellActivate(rowId, field, badgeInfo.editableText, badgeInfo.badge)}
          >
            {highlight(fullText, scope, badgeInfo.badge.length)}
          </td>
        );
      }
    }

    const rawValue = field === 'nodeOrSpeaker' ? row.nodeOrSpeaker : field === 'text' ? row.text : row.description;
    const cellBadge = extractReadonlyCommandBadge(row, field);

    if (isActive) {
      return (
        <EditableCell
          key={`${rowId}-${field}-active`}
          initialValue={rawValue}
          tdClassName={`${css.editableCell} ${dirty ? css.dirtyCell : ''} ${hasErrors ? css.validationErrorCell : ''}`}
          onCommit={onCellCommit}
          onCancel={onCellCancel}
        />
      );
    }

    if (cellBadge) {
      return (
        <td
          className={`${css.editableCell} ${dirty ? css.dirtyCell : ''} ${hasErrors ? css.validationErrorCell : ''}`}
          onClick={() => onCellActivate(rowId, field, rawValue)}
        >
          {highlight(rawValue, scope, cellBadge.badge.length, cellBadge.color)}
        </td>
      );
    }

    return (
      <td
        className={`${css.editableCell} ${dirty ? css.dirtyCell : ''} ${hasErrors ? css.validationErrorCell : ''}`}
        onClick={() => onCellActivate(rowId, field, rawValue)}
      >
        {highlight(rawValue, scope)}
      </td>
    );
  };

  const renderStepRow = (row: CsvEditRow) => {
    const editability = getEditability(row.type);
    const indent = isIndented(row.type);
    const hasErrors = row.errors.length > 0;
    const dirty = isCellDirty(row.id, 'nodeOrSpeaker') || isCellDirty(row.id, 'text') || isCellDirty(row.id, 'description');

    return (
      <tr
        key={row.id}
        className={`${css.nestedStep} ${indent ? css.indent1 : ''} ${hasErrors ? css.errorRow : ''} ${dirty ? css.dirtyRow : ''}`}
        title={hasErrors ? row.errors.join('\n') : row.warning}
      >
        {renderCell(row, 'nodeOrSpeaker', editability.speaker)}
        {renderCell(row, 'text', editability.text)}
          {renderCell(row, 'description', editability.description)}
      </tr>
    );
  };

  return (
    <div className={`${css.stepAccordionItem} ${isHighlighted ? css.highlightedNode : ''} ${isSelected ? css.selectedNode : ''}`} data-node-id={nodeId}>
      {/* Node header */}
      <div
        className={css.nodeAccordionHeader}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => onToggle(nodeId)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(nodeId); } }}
      >
        {/* Chevron */}
        <span className={`${css.nodeAccordionChevron} ${expanded ? css.nodeAccordionChevronExpanded : ''}`}>
          &#9654;
        </span>

        {/* Left section: node name + location */}
        <span className={css.nodeHeaderLeft}>
          <span className={css.nodeTitle}>
            {highlight(group.nodeRow.nodeOrSpeaker, 'node')}
          </span>
          {group.nodeRow.description && (
            <span className={css.metaBadge}>{group.nodeRow.description}</span>
          )}
        </span>

        {/* Middle section: always rendered to hold grid column — shows command badges */}
        <span className={css.nodeHeaderMiddle}>
          {stepTypeBadges.map((b) => (
            <span
              key={b.label}
              className={css.stepTypeBadge}
              style={{ background: b.color }}
            >
              {b.label}
            </span>
          ))}
        </span>

        {/* Right section: status badges + action buttons */}
        <span className={css.nodeHeaderRight}>
          {nodeDirty && <span className={css.dirtyBadge}>Modified</span>}
          {nodeHasErrors && <span className={css.errorBadge}>Errors</span>}
          {nodeDirty && (
            <button
              type="button"
              className={css.revertNodeButton}
              onClick={(e) => { e.stopPropagation(); onRevertNode(nodeId); }}
              title="Revert node to original"
              aria-label="Revert node to original"
            >
              &#8634;
            </button>
          )}
          {onNavigateToNode && (
            <button
              type="button"
              className={css.navigateNodeButton}
              onClick={(e) => { e.stopPropagation(); onNavigateToNode(group.nodeRow.nodeOrSpeaker); }}
              title="Go to node"
              aria-label="Go to node on canvas"
            >
              &#8599;
            </button>
          )}
        </span>
      </div>

      {/* Expanded step rows */}
      {expanded && group.stepRows.length > 0 && (
        <table className={`${css.nestedTable} ${css.accordionContent}`}>
          <tbody>
            <tr key={`${group.nodeRow.id}-props`} className={css.nodePropertyRow}>
              <td className={css.nodePropertyLabel}>Node</td>
              {renderCell(group.nodeRow, 'nodeOrSpeaker', false)}
              {renderCell(group.nodeRow, 'description', getEditability(group.nodeRow.type).description)}
            </tr>
            {group.stepRows.map((row) => renderStepRow(row))}
          </tbody>
        </table>
      )}

      {expanded && group.stepRows.length === 0 && (
        <div className={`${css.emptyState} ${css.accordionContent}`} style={{ padding: '8px 16px', fontSize: '13px' }}>
          No steps in this node.
        </div>
      )}

      {expanded && group.stepRows.some((row) => row.warning) && (
        <div className="text-warning" role="status">
          {group.stepRows.filter((row) => row.warning).map((row) => <div key={row.id}>{row.warning}</div>)}
        </div>
      )}
      {/* Error summary */}
      {expanded && nodeHasErrors && (() => {
        const allErrors: { source: string; error: string }[] = [];
        group.nodeRow.errors.forEach((err) => allErrors.push({ source: group.nodeRow.nodeOrSpeaker, error: err }));
        group.stepRows.forEach((row) => row.errors.forEach((err) => allErrors.push({ source: row.nodeOrSpeaker || row.type || row.id, error: err })));
        if (allErrors.length === 0) return null;
        return (
          <div className={css.errorSummary}>
            {allErrors.map((e, i) => (
              <div key={`${e.source}-${i}`} className={css.errorMessage}>{e.source}: {e.error}</div>
            ))}
          </div>
        );
      })()}
    </div>
  );
}

// --- Skeleton ---

const SKELETON_ROW_COUNT = 8;

// --- Component ---

export function TableEditor({
  nodeGroups,
  onUpdateRowField,
  isCellDirty,
  hasNodeErrors,
  isNodeDirty,
  onRevertNode,
  onNavigateToNode,
  onFilteredCountChange,
  isLoading = false,
  isReadOnly = false,
  initialNodeId,
}: TableEditorProps) {
  const [persistedFilters] = useState(() => {
    if (initialNodeId) return {};
    return readPersistedFilters();
  });
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(
    () => new Set(persistedFilters.expandedNodeIds ?? []),
  );
  const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [filterTokens, setFilterTokens] = useState<FilterToken[]>(persistedFilters.filterTokens ?? []);
  const [liveInput, setLiveInput] = useState('');
  const [debouncedLiveInput, setDebouncedLiveInput] = useState('');
  const [searchMode, setSearchMode] = useState<SearchMode>(persistedFilters.searchMode ?? 'plain');
  const [caseSensitive, setCaseSensitive] = useState(persistedFilters.caseSensitive ?? false);
  const [quickFilter, setQuickFilter] = useState<'all' | 'errors' | 'changed'>(persistedFilters.quickFilter ?? 'all');
  const [stepTypeFilter, setStepTypeFilter] = useState<EpisodeTextEntityType | 'all'>(persistedFilters.stepTypeFilter ?? 'all');
  const [activeCell, setActiveCell] = useState<ActiveCell | null>(null);
  const activationIdRef = useRef(0);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Search history
  const { history: searchHistory, addEntry: addSearchEntry, clearHistory: clearSearchHistory } = useFilterHistory('selective-edit-search-history');

  // Track the first visible node id for scroll restoration
  const lastVisibleNodeIdRef = useRef<string | null>(persistedFilters.scrollNodeId ?? null);
  const lastVisibleNodeOffsetRef = useRef<number>(persistedFilters.scrollNodeOffset ?? 0);

  // Save scroll position and expanded nodes on unmount
  const expandedNodesRef = useRef(expandedNodes);
  expandedNodesRef.current = expandedNodes;
  useEffect(() => () => {
    // Don't overwrite saved scroll when unmounting the loading skeleton
    if (isLoading) return;

    const saved = readPersistedFilters();
    writePersistedFilters({
      ...saved,
      scrollNodeId: lastVisibleNodeIdRef.current ?? undefined,
      scrollNodeOffset: lastVisibleNodeOffsetRef.current,
      expandedNodeIds: Array.from(expandedNodesRef.current),
    });
  }, []);

  // Persist filter state to localStorage on change
  useEffect(() => {
    writePersistedFilters({
      filterTokens,
      searchMode,
      caseSensitive,
      quickFilter,
      stepTypeFilter,
      scrollNodeId: lastVisibleNodeIdRef.current ?? undefined,
      scrollNodeOffset: lastVisibleNodeOffsetRef.current,
      expandedNodeIds: Array.from(expandedNodes),
    });
  }, [filterTokens, searchMode, caseSensitive, quickFilter, stepTypeFilter]);

  // Auto-focus search input when data loads (skip when jumping to a node)
  const didAutoFocus = useRef(false);
  useEffect(() => {
    if (nodeGroups.length > 0 && !didAutoFocus.current) {
      didAutoFocus.current = true;
      if (!initialNodeId) {
        const timer = setTimeout(() => searchInputRef.current?.focus(), 100);
        return () => clearTimeout(timer);
      }
    }
  }, [nodeGroups.length, initialNodeId]);

  const activeCellRef = useRef(activeCell);
  activeCellRef.current = activeCell;

  // Debounce live input for real-time filtering while typing
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedLiveInput(liveInput), 300);
    return () => clearTimeout(timer);
  }, [liveInput]);

  // Build active tokens: committed chips + live input as a temporary token
  const activeTokens = useMemo(() => {
    const trimmed = debouncedLiveInput.trim();
    if (trimmed.length < MIN_SEARCH_LENGTH) return filterTokens;
    const liveTokens = parseFilterTokens(trimmed);
    if (liveTokens.length === 0) return filterTokens;
    return [...filterTokens, ...liveTokens.map((t) => ({ ...t, id: `__live_${t.id}` }))];
  }, [filterTokens, debouncedLiveInput]);

  // Token-based matcher
  const tokenMatcher = useMemo(() => buildTokenMatcher(activeTokens, searchMode, caseSensitive), [activeTokens, searchMode, caseSensitive]);

  // Per-scope highlight matchers: each prefix highlights only in its target field.
  // 'general' tokens highlight everywhere. 'node:' highlights in node name cells.
  // 'step:' highlights in step content cells. 'type:' has no text highlight.
  const scopedMatchers = useMemo(() => {
    const generalValues = activeTokens.filter((t) => t.prefix === 'general').map((t) => t.value);
    const nodeValues = activeTokens.filter((t) => t.prefix === 'node').map((t) => t.value);
    const stepValues = activeTokens.filter((t) => t.prefix === 'step').map((t) => t.value);
    const speakerValues = activeTokens.filter((t) => t.prefix === 'speaker').map((t) => t.value);

    return {
      node: buildMatcher([...generalValues, ...nodeValues].join(' '), searchMode, caseSensitive),
      step: buildMatcher([...generalValues, ...stepValues].join(' '), searchMode, caseSensitive),
      speaker: buildMatcher([...generalValues, ...speakerValues].join(' '), searchMode, caseSensitive),
      description: buildMatcher(generalValues.join(' '), searchMode, caseSensitive),
    };
  }, [activeTokens, searchMode, caseSensitive]);


  const handleSelectHistory = useCallback((entry: SearchHistoryEntry) => {
    const parsed = parseFilterTokens(entry.query);
    if (parsed.length > 0) {
      setFilterTokens(parsed);
    }
    setSearchMode(entry.mode);
    setCaseSensitive(entry.caseSensitive);
    setLiveInput('');
    setDebouncedLiveInput('');
  }, []);

  const handleCommitToHistory = useCallback((raw: string) => {
    addSearchEntry(raw, searchMode, caseSensitive);
  }, [addSearchEntry, searchMode, caseSensitive]);

  const hasAnyActiveFilter = filterTokens.length > 0 || liveInput !== '' || quickFilter !== 'all'
    || stepTypeFilter !== 'all';

  const pendingScrollNodeId = useRef<string | null>(null);
  const skipVisibleResetRef = useRef(!!initialNodeId);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Shared scroll-to-node: finds node in nodeGroups, sets visibleCount, pending scroll, highlight.
  const scrollToNode = useCallback((targetId: string) => {
    const targetIndex = nodeGroups.findIndex((g) => g.nodeRow.id === targetId);
    if (targetIndex >= 0) {
      const needed = targetIndex + PAGE_SIZE;
      if (needed > visibleCount) {
        setVisibleCount(needed);
        skipVisibleResetRef.current = true;
      }
    }
    setExpandedNodes((prev) => new Set(prev).add(targetId));
    setHighlightedNodeId(targetId);
    pendingScrollNodeId.current = targetId;
  }, [nodeGroups, visibleCount]);

  // Scroll to selected node, or to the nearest node above it in the full list
  // if the selected node is hidden by remaining filters (e.g. step type).
  const scrollToSelectedOrNearest = useCallback(() => {
    if (!selectedNodeId) return;
    // After filters are reset in the same call, filteredGroups hasn't recomputed yet.
    // scrollToNode uses nodeGroups (full list), so selected node will be found
    // as long as all filters are cleared. For partial clears (e.g. × button only),
    // the node may still be hidden — scrollToNode will still position correctly
    // because it sets visibleCount based on full nodeGroups index.
    scrollToNode(selectedNodeId);
  }, [scrollToNode, selectedNodeId]);

  const handleTokensChange = useCallback((newTokens: FilterToken[]) => {
    setFilterTokens(newTokens);
    if (newTokens.length === 0) {
      scrollToSelectedOrNearest();
    }
  }, [scrollToSelectedOrNearest]);

  const resetAllFilters = useCallback(() => {
    setFilterTokens([]);
    setLiveInput('');
    setDebouncedLiveInput('');
    setSearchMode('plain');
    setCaseSensitive(false);
    setQuickFilter('all');
    setStepTypeFilter('all');
    searchInputRef.current?.focus();
    scrollToSelectedOrNearest();
  }, [scrollToSelectedOrNearest]);

  // No auto-expand — user controls expand/collapse manually.
  const effectiveExpanded = expandedNodes;

  const filteredGroups = useMemo(() => {
    let groups = nodeGroups;
    if (quickFilter === 'errors') groups = groups.filter((g) => hasNodeErrors(g.nodeRow.id));
    else if (quickFilter === 'changed') groups = groups.filter((g) => isNodeDirty(g.nodeRow.id));
    if (tokenMatcher.active) {
      groups = groups.filter((g) => tokenMatcher.test(g));
    }
    if (stepTypeFilter !== 'all') {
      groups = groups.filter((g) => g.stepRows.some((r) => r.type === stepTypeFilter));
    }
    return groups;
  }, [nodeGroups, quickFilter, hasNodeErrors, isNodeDirty, tokenMatcher, stepTypeFilter]);

  const didScrollToInitial = useRef(false);
  useEffect(() => {
    if (!initialNodeId || didScrollToInitial.current || filteredGroups.length === 0) return;

    const exists = filteredGroups.some((g) => g.nodeRow.id === initialNodeId);
    if (!exists) return;

    didScrollToInitial.current = true;
    scrollToNode(initialNodeId);
  }, [initialNodeId, filteredGroups, scrollToNode]);

  // Scroll to pending node after React renders — centers the node in tableFrame
  useLayoutEffect(() => {
    const nodeId = pendingScrollNodeId.current;
    if (!nodeId) return;
    const frame = tableFrameRef.current;
    if (!frame) return;
    const el = frame.querySelector<HTMLElement>(`[data-node-id="${nodeId}"]`);
    if (!el) return;
    pendingScrollNodeId.current = null;
    const elOffsetTop = el.offsetTop - frame.offsetTop;
    const targetScroll = elOffsetTop - (frame.clientHeight / 2) + (el.offsetHeight / 2);
    frame.scrollTo({ top: Math.max(0, targetScroll), behavior: 'smooth' });
  });

  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (skipVisibleResetRef.current) {
      skipVisibleResetRef.current = false;
      return;
    }
    setVisibleCount(PAGE_SIZE);
  }, [quickFilter, tokenMatcher, stepTypeFilter]);

  // IntersectionObserver — load next page when sentinel is visible
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((prev) => {
            const next = Math.min(prev + PAGE_SIZE, filteredGroups.length);
            return next;
          });
        }
      },
      // rootMargin expands the detection zone 400px below the viewport —
      // next batch starts loading before the user reaches the bottom
      { rootMargin: '0px 0px 400px 0px', threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [filteredGroups.length]);

  // Track first visible node on scroll for position restoration
  useEffect(() => {
    const frame = tableFrameRef.current;
    if (!frame) return;
    const onScroll = () => {
      const frameRect = frame.getBoundingClientRect();
      const items = frame.querySelectorAll<HTMLElement>('[data-node-id]');
      for (const item of items) {
        const header = item.firstElementChild as HTMLElement | null;
        const anchor = header ?? item;
        const anchorTop = anchor.getBoundingClientRect().top;
        if (anchorTop >= frameRect.top - 5) {
          lastVisibleNodeIdRef.current = item.dataset.nodeId ?? null;
          lastVisibleNodeOffsetRef.current = anchorTop - frameRect.top;
          return;
        }
      }
    };
    frame.addEventListener('scroll', onScroll, { passive: true });
    return () => frame.removeEventListener('scroll', onScroll);
  });

  // Restore scroll position on reopen (skip if jumping to a specific node)
  const didRestoreScroll = useRef(false);
  useLayoutEffect(() => {
    if (initialNodeId || didRestoreScroll.current) return;
    const savedId = persistedFilters.scrollNodeId;
    if (!savedId || filteredGroups.length === 0) return;

    const targetIndex = filteredGroups.findIndex((g) => g.nodeRow.id === savedId);
    if (targetIndex < 0) return;

    const needed = targetIndex + PAGE_SIZE;
    if (needed > visibleCount) {
      setVisibleCount(needed);
      return; // will re-run after visibleCount updates
    }

    const frame = tableFrameRef.current;
    if (!frame) return;
    const el = frame.querySelector<HTMLElement>(`[data-node-id="${savedId}"]`);
    if (!el) return;

    didRestoreScroll.current = true;
    const header = el.firstElementChild as HTMLElement | null;
    const anchor = header ?? el;
    const frameRect = frame.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();
    const savedOffset = persistedFilters.scrollNodeOffset ?? 0;
    frame.scrollTop += (anchorRect.top - frameRect.top) - savedOffset;
  });

  const visibleGroups = filteredGroups.slice(0, visibleCount);
  const hasMore = visibleCount < filteredGroups.length;

  // Notify parent about filtered count for footer badge
  useEffect(() => {
    onFilteredCountChange?.(filteredGroups.length);
  }, [filteredGroups.length, onFilteredCountChange]);

  const blurActiveEdit = useCallback(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, []);

  const toggleNode = useCallback((nodeId: string) => {
    setSelectedNodeId(nodeId);
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
        blurActiveEdit();
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, [blurActiveEdit]);

  const tableFrameRef = useRef<HTMLDivElement>(null);
  const scrollAnchorRef = useRef<{ id: string; offset: number } | null>(null);

  const captureScrollAnchor = useCallback(() => {
    const frame = tableFrameRef.current;
    if (!frame) return;
    const frameRect = frame.getBoundingClientRect();
    const centerY = frameRect.top + frameRect.height / 2;
    const items = frame.querySelectorAll<HTMLElement>('[data-node-id]');
    let best: { id: string; offset: number } | null = null;
    let bestDist = Infinity;
    for (const item of items) {
      const rect = item.getBoundingClientRect();
      const itemCenter = rect.top + rect.height / 2;
      const dist = Math.abs(itemCenter - centerY);
      if (dist < bestDist) {
        bestDist = dist;
        best = { id: item.dataset.nodeId!, offset: rect.top - frameRect.top };
      }
    }
    scrollAnchorRef.current = best;
  }, []);

  // useLayoutEffect runs synchronously after DOM mutation but before paint —
  // corrects scroll position so the anchor node doesn't visually jump.
  useLayoutEffect(() => {
    const anchor = scrollAnchorRef.current;
    if (!anchor) return;
    scrollAnchorRef.current = null;
    const frame = tableFrameRef.current;
    if (!frame) return;
    const el = frame.querySelector<HTMLElement>(`[data-node-id="${anchor.id}"]`);
    if (!el) return;
    const newOffset = el.getBoundingClientRect().top - frame.getBoundingClientRect().top;
    frame.scrollTop += newOffset - anchor.offset;
  }, [expandedNodes]);

  const expandAll = useCallback(() => {
    captureScrollAnchor();
    setExpandedNodes(new Set(filteredGroups.map((g) => g.nodeRow.id)));
  }, [filteredGroups, captureScrollAnchor]);

  const collapseAll = useCallback(() => {
    blurActiveEdit();
    captureScrollAnchor();
    setExpandedNodes(new Set());
  }, [blurActiveEdit, captureScrollAnchor]);

  // Shared scroll-to-node: finds node in nodeGroups, sets visibleCount, pending scroll, highlight.
  // Used by Alt+E initial scroll, goto badge click, and filter reset.
  // Goto badge click handler
  const [gotoNotice, setGotoNotice] = useState<{ message: string; x: number; y: number } | null>(null);

  const handleGotoClick = useCallback((targetNodeName: string, event?: React.MouseEvent) => {
    const cleanName = targetNodeName.replace(/^#goto\s+/i, '').replace(/^→\s*/, '').trim();
    const needle = cleanName.toLowerCase();

    const targetGroup = filteredGroups.find(
      (g) => g.nodeRow.nodeOrSpeaker.toLowerCase().trim() === needle || g.nodeNameText.toLowerCase().trim() === needle,
    );
    if (!targetGroup) {
      const existsInAll = nodeGroups.some(
        (g) => g.nodeRow.nodeOrSpeaker.toLowerCase().trim() === needle || g.nodeNameText.toLowerCase().trim() === needle,
      );
      if (existsInAll && event) {
        const rect = (event.target as HTMLElement).getBoundingClientRect();
        setGotoNotice({ message: `"${cleanName}" is hidden by the active filter`, x: rect.left, y: rect.bottom + 4 });
      }
      return;
    }

    setGotoNotice(null);
    setSelectedNodeId(targetGroup.nodeRow.id);
    scrollToNode(targetGroup.nodeRow.id);
  }, [filteredGroups, nodeGroups, scrollToNode]);

  // Ctrl+] (Cmd+] on Mac) expand all, Ctrl+[ (Cmd+[ on Mac) collapse all
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      if (e.key === ']') {
        e.preventDefault();
        expandAll();
      } else if (e.key === '[') {
        e.preventDefault();
        collapseAll();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [expandAll, collapseAll]);

  const activateCell = useCallback((rowId: string, field: EditableField, _currentValue: string, badgePrefix?: string) => {
    activationIdRef.current += 1;
    setActiveCell({ rowId, field, badgePrefix });
  }, []);

  const handleCellCommit = useCallback((value: string) => {
    const cell = activeCellRef.current;
    if (!cell) return;
    onUpdateRowField(cell.rowId, cell.field, value);
    setActiveCell(null);
  }, [onUpdateRowField]);

  const handleCellCancel = useCallback(() => {
    setActiveCell(null);
  }, []);

  const searchHelperText = useMemo(() => {
    if (tokenMatcher.error) return tokenMatcher.error;
    if (searchMode === 'regex' && activeTokens.length > 0) {
      return 'Regular expression active';
    }
    if (searchMode === 'regex') {
      return 'Enter a regular expression (min 2 characters)';
    }
    return '\u00A0';
  }, [tokenMatcher.error, searchMode, activeTokens.length]);

  // Auto-dismiss goto notice
  useEffect(() => {
    if (!gotoNotice) return;
    const timer = setTimeout(() => setGotoNotice(null), 3000);
    return () => clearTimeout(timer);
  }, [gotoNotice]);

  return (
    <div className={css.container}>
      {/* Goto notice popover — single element, positioned near click target */}
      {gotoNotice && (
        <div
          className={css.gotoNotice}
          style={{ left: gotoNotice.x, top: gotoNotice.y }}
          onClick={() => setGotoNotice(null)}
        >
          {gotoNotice.message}
        </div>
      )}
      {/* Filters toolbar */}
      <div className={css.filters}>
        <Form.Group className={css.filterItem}>
          <Form.Label className={css.filterLabel}>Search</Form.Label>
          <SmartFilterInput
            tokens={filterTokens}
            onTokensChange={handleTokensChange}
            searchMode={searchMode}
            caseSensitive={caseSensitive}
            onSearchModeChange={setSearchMode}
            onCaseSensitiveChange={setCaseSensitive}
            hasError={!!tokenMatcher.error}
            searchHistory={searchHistory}
            onSelectHistory={handleSelectHistory}
            onClearHistory={clearSearchHistory}
            onCommitToHistory={handleCommitToHistory}
            onInputChange={setLiveInput}
            inputRef={searchInputRef}
          />
          <div
            className={`${css.searchHelperRow} ${tokenMatcher.error ? css.searchHelperError : ''}`}
            data-testid="search-helper-row"
            aria-hidden={!tokenMatcher.error && searchMode === 'plain' ? true : undefined}
          >
            {searchHelperText}
          </div>
        </Form.Group>

        <Form.Group className={css.filterItem}>
          <Form.Label className={css.filterLabel}>Filter</Form.Label>
          <select
            className="form-control form-control-sm"
            value={quickFilter}
            onChange={(e) => { setQuickFilter(e.target.value as 'all' | 'errors' | 'changed'); scrollToSelectedOrNearest(); }}
          >
            <option value="all">All nodes</option>
            <option value="errors">With errors</option>
            <option value="changed">Unsaved Edits</option>
          </select>
        </Form.Group>
        <Form.Group className={css.filterItem}>
          <Form.Label className={css.filterLabel}>Step type</Form.Label>
          <select
            className="form-control form-control-sm"
            value={stepTypeFilter}
            onChange={(e) => { setStepTypeFilter(e.target.value as EpisodeTextEntityType | 'all'); scrollToSelectedOrNearest(); }}
          >
            <option value="all">All types</option>
            {Object.entries(STEP_BADGE_CONFIG).map(([type, cfg]) => (
              <option key={type} value={type}>{cfg!.label}</option>
            ))}
          </select>
        </Form.Group>
        <Button
          variant={hasAnyActiveFilter ? 'primary' : 'secondary'}
          size="sm"
          onClick={resetAllFilters}
          disabled={!hasAnyActiveFilter}
          title="Reset all filters"
        >
          Clear Filters
        </Button>
        <div className={css.filtersSpacer} />
        <Button variant="outline-secondary" size="sm" onClick={expandAll}>Expand All</Button>
        <Button variant="outline-secondary" size="sm" onClick={collapseAll}>Collapse All</Button>
      </div>

      {filteredGroups.length === 0 && isLoading ? (
        /* Skeleton rows while fetch+parse is in progress */
        <div className={css.tableFrame}>
          <table className={css.nestedTable}>
            <tbody>
              {Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => (
                <tr key={i} className={css.skeletonRow}>
                  <td><div className={css.skeletonCell} style={{ width: `${40 + (i % 3) * 15}%` }} /></td>
                  <td><div className={css.skeletonCell} style={{ width: `${55 + (i % 4) * 10}%` }} /></td>
                  <td><div className={css.skeletonCell} style={{ width: `${30 + (i % 3) * 12}%` }} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : filteredGroups.length === 0 ? (
        <div className={css.emptyState}>No data to display.</div>
      ) : (
        <div className={css.tableFrame} ref={tableFrameRef}>
          {visibleGroups.map((group) => {
            const nodeId = group.nodeRow.id;
            return (
              <NodeAccordionItem
                key={nodeId}
                group={group}
                expanded={effectiveExpanded.has(nodeId)}
                nodeDirty={isNodeDirty(nodeId)}
                nodeHasErrors={hasNodeErrors(nodeId)}
                scopedMatchers={scopedMatchers}
                caseSensitive={caseSensitive}
                isHighlighted={highlightedNodeId === nodeId}
                isSelected={selectedNodeId === nodeId}
                activeCellRef={activeCellRef}
                isCellDirty={isCellDirty}
                onToggle={toggleNode}
                onRevertNode={onRevertNode}
                onNavigateToNode={onNavigateToNode}
                onCellActivate={activateCell}
                onCellCommit={handleCellCommit}
                onCellCancel={handleCellCancel}
                onGotoClick={handleGotoClick}
                isReadOnly={isReadOnly}
              />
            );
          })}
          {/* Sentinel for IntersectionObserver — triggers next page load */}
          {hasMore && <div ref={sentinelRef} style={{ height: 1 }} aria-hidden />}
        </div>
      )}
    </div>
  );
}
